import { useEffect, useMemo, useRef, useState } from "react";
import RadarProfile from "./RadarProfile";
import { rtctApi } from "services/rtctApi";
import {
  BLIND_DEFAULT_MM,
  RADAR_FIRMWARE,
  atText,
  capturedAt,
  commandInFlight,
  stillAsked,
  describeRequest,
  echoList,
  finite,
  formatMm as mm,
  newer,
  newestWith,
  onCellular,
  parseBlindZone,
  pollValue,
  radarCommandError,
  radarFirmwareSupport,
  readingFromSample,
  sgtTime as sgt,
  statusText,
  timeOf,
  trackRequest,
} from "utils/radarDiagnostics";

// Radar diagnostics (the tools on Radar analysis): what the sensor itself
// reports (its settings, every poll, its echo list), the blind-zone test and
// the echo profile. Commands go through the ordinary settings endpoint, one
// at a time, and run on the node's next wake.
const STALE_MS = 24 * 3600 * 1000;
const REQUEST_STATUS = {
  queued: "being sent",
  sent: "waiting for the node to pick it up",
  acknowledged: "received by the node, which acts on its next wake",
  confirmed: "confirmed by the node",
  not_applied: "not applied: the node reports other values",
  publish_failed: "could not reach the broker",
  superseded: "replaced by a newer request",
  rejected: "rejected by the node",
};
const requestText = (status) =>
  REQUEST_STATUS[status] || String(status || "sent").replaceAll("_", " ");
const sentence = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const positive = (text) => {
  const n = finite(String(text).trim());
  return n !== null && n > 0 ? n : null;
};

// Per node, in this browser only: the expected floor depth and pipe inner
// diameter typed here, and the blind zone read back before the last change
// sent from here (for "Restore previous value").
const siteKey = (mac) => `field_radar:${mac}`;
function loadSite(mac) {
  try {
    const value = JSON.parse(localStorage.getItem(siteKey(mac)) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}
function saveSite(mac, patch) {
  try {
    const next = { ...loadSite(mac), ...patch };
    Object.keys(next).forEach((key) => {
      if (next[key] === null || next[key] === undefined) delete next[key];
    });
    if (Object.keys(next).length)
      localStorage.setItem(siteKey(mac), JSON.stringify(next));
    else localStorage.removeItem(siteKey(mac));
  } catch {}
}

// The radar's own settings as the node read them back (radar_cfg): here
// beside the tools, and as Sensor settings on Node Control.
export function SensorSettings({ cfg, cfgAt = null, id = "field-radar-settings" }) {
  const rows = [
    ["Sensor firmware", cfg?.ver !== undefined && cfg?.ver !== null ? String(cfg.ver) : null],
    ["Blind zone", cfg?.blind_mm, "mm"],
    ["Range", cfg?.range_mm, "mm"],
    ["Calibration offset", cfg?.cal_mm, "mm"],
    ["Install height", cfg?.inst_mm, "mm"],
    ["Sampling period", cfg?.cf_ms, "ms"],
    [
      "Push output",
      cfg?.push_ms === 0 ? "Off" : cfg?.push_ms,
      cfg?.push_ms === 0 ? "" : "ms",
    ],
  ];
  return (
    <div role="group" aria-labelledby={id}>
      <h3 id={id} className="field-radar-heading">
        Sensor settings
      </h3>
      {cfg ? (
        <>
          <dl className="field-facts field-radar-facts">
            {rows.map(([label, value, unit]) => (
              <div className="field-fact" key={label}>
                <dt>{label}</dt>
                <dd>
                  {value === undefined || value === null
                    ? "Not read"
                    : typeof value === "number"
                      ? `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit ? ` ${unit}` : ""}`
                      : value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="field-caption">
            {cfgAt
              ? `Read back from the sensor; reported ${sgt(cfgAt)}.`
              : "Read back from the sensor."}{" "}
            The node reads them on its first wake after a restart and
            with every radar command, and reports them when they change.
          </p>
        </>
      ) : (
        <p className="field-caption">
          Not read back yet. The node reads the sensor's own settings on
          its first wake after a restart with firmware {RADAR_FIRMWARE}.
        </p>
      )}
    </div>
  );
}

export default function RadarPanel({
  mac,
  samples = [],
  settings = null,
  refresh = 0,
  onSent,
}) {
  const [radar, setRadar] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tick, setTick] = useState(0);
  const [floorText, setFloorText] = useState(() =>
    String(loadSite(mac).floor_mm ?? ""),
  );
  const [pipeText, setPipeText] = useState(() =>
    String(loadSite(mac).pipe_mm ?? ""),
  );
  const [previousBlind, setPreviousBlind] = useState(() => {
    const v = finite(loadSite(mac).blind_previous_mm);
    return v !== null && v > 0 ? v : null;
  });
  const [blindText, setBlindText] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [blindBusy, setBlindBusy] = useState(false);
  const [blindError, setBlindError] = useState("");
  const [blindLocal, setBlindLocal] = useState(null);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureError, setCaptureError] = useState("");
  const [captureLocal, setCaptureLocal] = useState(null);
  const alive = useRef(true);
  const confirmButton = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (!mac) return;
    const abort = new AbortController();
    rtctApi
      .radar(mac, abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        setRadar(data);
        setLoadError(null);
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setLoadError({ message: e.message, status: e.status });
      });
    return () => abort.abort();
  }, [mac, refresh, tick]);
  useEffect(() => {
    if (confirm !== null) confirmButton.current?.focus();
  }, [confirm]);

  // ---- What the node and the sensor report ----
  const newestSample = useMemo(
    () =>
      samples.reduce(
        (best, s) =>
          (timeOf(s?.timestamp) ?? -Infinity) >
          (timeOf(best?.timestamp) ?? -Infinity)
            ? s
            : best,
        null,
      ),
    [samples],
  );
  const fw = newestSample?.raw?.fw ?? newestSample?.raw?.firmware;
  const support = radarFirmwareSupport(fw);
  const cellular = onCellular(newestSample?.raw);
  const latest = radar?.latest || null;
  const cfg = radar?.radar_cfg || settings?.radar_cfg || null;
  const cfgAt = radar?.radar_cfg ? radar.radar_cfg_at : null;
  const reading = latest
    ? {
        t: latest.t,
        distance_mm: latest.distance_mm,
        status: latest.status,
        polls: latest.polls,
      }
    : readingFromSample(newestSample);
  const fromLatest = (key) =>
    latest?.[key] ? { t: latest.t, value: latest[key] } : null;
  const echo = newer(newestWith(samples, "jjy_pk"), fromLatest("peaks"));
  const blindResult = newer(newestWith(samples, "jjy_bz"), fromLatest("blind"));
  const fft = newer(newestWith(samples, "jjy_fft"), fromLatest("fft"));
  const echoes = echoList(echo?.value);
  // The blind zone in force: the radar's settings as read back (the node
  // reads them again right after a change), else the last change's read-back.
  const current =
    finite(cfg?.blind_mm) !== null
      ? { t: cfgAt, value: finite(cfg.blind_mm), fromCfg: true }
      : finite(blindResult?.value?.rb) !== null
        ? { t: blindResult.t, value: finite(blindResult.value.rb) }
        : null;
  // One-shot captures only: streamed ones (live mode) and those of rich-wake
  // bursts fill the 20 newest and are shown by Live stream and Rich wakes
  // from their own endpoints.
  const captures = useMemo(
    () =>
      [...(radar?.captures || [])]
        .filter((c) => c.mode !== "stream" && c.mode !== "burst")
        .sort((a, b) => (timeOf(capturedAt(b)) ?? 0) - (timeOf(capturedAt(a)) ?? 0)),
    [radar],
  );
  const streamed = (radar?.captures || []).filter((c) => c.mode === "stream").length;
  const bursted = (radar?.captures || []).filter((c) => c.mode === "burst").length;

  // ---- Commands and how far they got ----
  const blindReq = trackRequest(blindLocal, settings, "radar_blind_mm");
  const blindCancel = !!blindReq && Number(blindReq.value) === 0;
  // A result for the requested value that is newer than the request (or any,
  // once the server has confirmed it: that can move `since` past the result).
  const blindDone =
    blindReq &&
    !blindCancel &&
    blindResult &&
    Number(blindResult.value.req) === Number(blindReq.value) &&
    (blindReq.status === "confirmed" ||
      (timeOf(blindResult.t) ?? -Infinity) >=
        (timeOf(blindReq.since) ?? Infinity) - 60000)
      ? blindResult.value
      : null;
  const captureTracked = trackRequest(captureLocal, settings, "radar_raw");
  const captureReq =
    captureTracked && Number(captureTracked.value) === 1 ? captureTracked : null;
  const captureSince = timeOf(captureReq?.since) ?? Infinity;
  const captured = captureReq
    ? captures.find(
        (c) =>
          (timeOf(c.received_at || c.captured_at) ?? -Infinity) >= captureSince,
      ) || (captureReq.status === "confirmed" ? captures[0] : null)
    : null;
  const captureRan =
    captureReq &&
    !captured &&
    (timeOf(fft?.t) ?? -Infinity) >= captureSince - 60000
      ? fft
      : null;
  // The node keeps only the newest command: the next waits until it arrived.
  const inFlight = commandInFlight(settings, blindReq, captureTracked);
  const unavailable = !cfg || support === false;
  const locked = unavailable || !!inFlight;
  // A request still to be settled ("not_applied" is settled), for a day.
  const fresh = (req) =>
    stillAsked(req, settings) &&
    Date.now() - (timeOf(req.since) ?? 0) < STALE_MS;
  const waiting =
    !!inFlight ||
    (fresh(blindReq) && !blindDone && !blindCancel) ||
    (fresh(captureReq) && !captured);

  const parsed = parseBlindZone(blindText);
  const sameAsNow =
    parsed.value !== undefined &&
    current &&
    Math.abs(parsed.value - current.value) < 0.5;
  const restoreTo = (() => {
    const ok = (v) => v !== null && parseBlindZone(String(v)).value !== undefined;
    if (ok(previousBlind) && (!current || Math.abs(previousBlind - current.value) >= 0.5))
      return { value: previousBlind, label: `Restore previous value (${mm(previousBlind)})` };
    if (current && Math.abs(current.value - BLIND_DEFAULT_MM) >= 0.5)
      return { value: BLIND_DEFAULT_MM, label: `Restore default (${mm(BLIND_DEFAULT_MM)})` };
    return null;
  })();
  const testValue = parsed.value ?? 200;
  const floorMm = positive(floorText);
  const readBack = current
    ? current.fromCfg
      ? current.t
        ? `read back from the sensor, reported ${sgt(current.t)}`
        : "read back from the sensor"
      : `read back after the change ${sgt(current.t)}`
    : "";

  const sendBlind = async (value) => {
    setBlindBusy(true);
    setBlindError("");
    try {
      const data = await rtctApi.applySettings(mac, { radar_blind_mm: value });
      if (!alive.current) return;
      if (previousBlind !== null && Math.abs(previousBlind - value) < 0.5) {
        // Restored: nothing left to restore.
        saveSite(mac, { blind_previous_mm: null });
        setPreviousBlind(null);
      } else if (current && Math.abs(current.value - value) >= 0.5) {
        const before = Math.round(current.value);
        saveSite(mac, { blind_previous_mm: before });
        setPreviousBlind(before);
      }
      setBlindLocal({
        key: "radar_blind_mm",
        value,
        request_id: data?.request_id ?? null,
        status: data?.status || "sent",
        since: data?.updated_at || new Date().toISOString(),
      });
      setConfirm(null);
      setBlindText("");
      setTick((n) => n + 1);
      onSent?.();
    } catch (e) {
      if (alive.current) setBlindError(radarCommandError(e));
    } finally {
      if (alive.current) setBlindBusy(false);
    }
  };
  const capture = async () => {
    setCaptureBusy(true);
    setCaptureError("");
    try {
      const data = await rtctApi.applySettings(mac, { radar_raw: 1 });
      if (!alive.current) return;
      setCaptureLocal({
        key: "radar_raw",
        value: 1,
        request_id: data?.request_id ?? null,
        status: data?.status || "sent",
        since: data?.updated_at || new Date().toISOString(),
      });
      setTick((n) => n + 1);
      onSent?.();
    } catch (e) {
      if (alive.current) setCaptureError(radarCommandError(e));
    } finally {
      if (alive.current) setCaptureBusy(false);
    }
  };
  const changeFloor = (text) => {
    setFloorText(text);
    if (text.trim() === "") saveSite(mac, { floor_mm: null });
    else if (positive(text) !== null) saveSite(mac, { floor_mm: Number(text) });
  };
  const changePipe = (text) => {
    setPipeText(text);
    if (text.trim() === "") saveSite(mac, { pipe_mm: null });
    else if (positive(text) !== null) saveSite(mac, { pipe_mm: Number(text) });
  };

  const [chip, tone] = waiting
    ? ["Waiting for the node", "is-waiting"]
    : unavailable
      ? ["Not available on this node yet", "is-off"]
      : reading?.status
        ? reading.status === "ok"
          ? ["Reading OK", ""]
          : [statusText(reading.status), "is-waiting"]
        : ["No reading yet", "is-off"];

  return (
    <section
      className="field-panel field-radar"
      aria-labelledby="field-radar-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="field-radar-title">Radar diagnostics</h2>
          <p>
            What the sensor itself reports, the blind-zone test and its echo
            profile · firmware {RADAR_FIRMWARE} or later
          </p>
        </div>
        <span className={`field-state ${tone}`.trim()} role="status">
          {chip}
        </span>
      </div>
      <div className="field-radar-grid">
        <div className="field-radar-summary">
          {loadError &&
            (loadError.status === 404 ? (
              <div className="field-notice">
                This server does not provide radar diagnostics yet. The reading
                and echo list below come from telemetry; captures need the
                updated backend.
              </div>
            ) : (
              <div className="field-alert" role="alert">
                {loadError.message}
              </div>
            ))}
          <div className="field-radar-reading">
            <span className="field-radar-label">Latest reading</span>
            {reading ? (
              <>
                <strong>
                  {finite(reading.distance_mm) !== null
                    ? `${finite(reading.distance_mm).toFixed(1)} mm`
                    : "No distance"}
                </strong>
                <small>
                  {statusText(reading.status)} · {sgt(reading.t)}
                </small>
                {Array.isArray(reading.polls) && reading.polls.length ? (
                  <ul className="field-radar-polls" aria-label="Every poll">
                    {reading.polls.map((v, i) => {
                      const poll = pollValue(v);
                      return (
                        <li
                          key={i}
                          className={poll.sentinel ? "is-sentinel" : undefined}
                        >
                          {poll.text}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="field-caption">
                    Every poll value is reported from firmware {RADAR_FIRMWARE}
                    .
                  </p>
                )}
              </>
            ) : (
              <p className="field-caption">
                No reading yet. The node reports one on every wake.
              </p>
            )}
          </div>
          <SensorSettings cfg={cfg} cfgAt={cfgAt} />
          <div role="group" aria-labelledby="field-radar-echoes">
            <h3 id="field-radar-echoes" className="field-radar-heading">
              Echo list
            </h3>
            {echoes?.echoes.length ? (
              <>
                <table className="field-table field-radar-echoes">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Range</th>
                      <th scope="col">Strength</th>
                    </tr>
                  </thead>
                  <tbody>
                    {echoes.echoes.map((e) => (
                      <tr key={e.rank}>
                        <td>{e.rank}</td>
                        <td>{mm(e.rangeMm)}</td>
                        <td>
                          {e.strength === null
                            ? "—"
                            : e.strength.toLocaleString(undefined, {
                                maximumFractionDigits: 1,
                              })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="field-caption">
                  The sensor's own list, strongest first ({echoes.count} found)
                  · {sgt(echo.t)} · strength in the sensor's units.
                </p>
              </>
            ) : echoes?.result && echoes.result !== "value" ? (
              <p className="field-caption">
                The sensor did not return an echo list ({atText(echoes.result)}
                ) · {sgt(echo.t)}.
              </p>
            ) : (
              <p className="field-caption">
                No echo list yet. The node asks the sensor for one on its first
                wake after a restart and with every echo-profile capture.
              </p>
            )}
          </div>
        </div>
        <div className="field-radar-controls">
          {unavailable && (
            <div className="field-notice">
              {support === false
                ? `This node runs ${fw}. Radar diagnostics need ${RADAR_FIRMWARE} or later; older firmware ignores these commands.`
                : `The radar's own settings have not been read back yet, so these controls are off. Firmware ${RADAR_FIRMWARE} or later reads them on its first wake after a restart (an update restarts the node) and reports them with its settings.`}
            </div>
          )}
          {inFlight && (
            <div className="field-radar-caution" role="note">
              {sentence(describeRequest(inFlight))} sent {sgt(inFlight.since)}{" "}
              is still on its way to the node ({requestText(inFlight.status)}).
              The node keeps only the newest command, so the next one can go
              once this one is acknowledged.
            </div>
          )}
          <div className="field-radar-block">
            <h3 className="field-radar-heading">Blind zone</h3>
            <p className="field-radar-now">
              {current ? (
                <>
                  Now <b>{mm(current.value, 1)}</b>
                  <small>{sentence(readBack)}</small>
                </>
              ) : (
                <>
                  Not read back yet
                  <small>
                    The sensor's default is {mm(BLIND_DEFAULT_MM)}; echoes
                    closer than the blind zone are ignored.
                  </small>
                </>
              )}
            </p>
            <div className="field-radar-row">
              <label htmlFor="radar-blind" className="field-radar-input">
                New blind zone (mm)
                <input
                  id="radar-blind"
                  type="number"
                  inputMode="numeric"
                  min={15}
                  max={1500}
                  step={1}
                  placeholder="200"
                  value={blindText}
                  disabled={blindBusy || unavailable}
                  aria-invalid={blindText !== "" && !!parsed.error}
                  onChange={(e) => {
                    setBlindText(e.target.value);
                    setConfirm(null);
                    setBlindError("");
                  }}
                />
              </label>
              <button
                type="button"
                className="field-button primary"
                disabled={
                  blindBusy ||
                  locked ||
                  !!parsed.error ||
                  sameAsNow ||
                  confirm !== null
                }
                onClick={() => setConfirm(parsed.value)}
              >
                Apply on next wake
              </button>
            </div>
            {blindText !== "" && parsed.error && (
              <small className="field-radar-hint">{parsed.error}</small>
            )}
            {sameAsNow && (
              <small className="field-radar-hint">
                That is the current blind zone.
              </small>
            )}
            {restoreTo && confirm === null && (
              <button
                type="button"
                className="field-button"
                disabled={blindBusy || locked}
                onClick={() => {
                  setBlindText(String(restoreTo.value));
                  setBlindError("");
                  setConfirm(restoreTo.value);
                }}
              >
                {restoreTo.label}
              </button>
            )}
            {confirm !== null && (
              <div
                className="field-radar-confirm"
                role="group"
                aria-labelledby="field-radar-confirm"
              >
                <p id="field-radar-confirm">
                  <b>Set the blind zone to {mm(confirm)}?</b>{" "}
                  {current
                    ? `It is ${mm(current.value, 1)} now (${readBack}). `
                    : ""}
                  The sensor stores it in its own memory and keeps it after
                  restarts until it is changed again; every echo closer than
                  that is ignored.
                </p>
                <div className="field-actions" style={{ marginTop: 10 }}>
                  <button
                    ref={confirmButton}
                    type="button"
                    className="field-button primary"
                    disabled={blindBusy || locked}
                    onClick={() => sendBlind(confirm)}
                  >
                    {blindBusy ? "Sending…" : `Confirm: set ${mm(confirm)}`}
                  </button>
                  <button
                    type="button"
                    className="field-button"
                    disabled={blindBusy}
                    onClick={() => setConfirm(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {blindError && (
              <div className="field-alert" role="alert">
                {blindError}
              </div>
            )}
            {blindReq && (
              <p className="field-radar-trail" role="status">
                {blindDone
                  ? `${blindReq.status === "not_applied" ? "Not applied" : "Applied"} ${sgt(blindResult.t)}: ${mm(blindReq.value)} requested, ${finite(blindDone.rb) !== null ? `the sensor reads back ${mm(blindDone.rb, 1)}` : "but the blind zone could not be read back"} (it answered ${atText(blindDone.r)}).${blindReq.status === "not_applied" ? ` The request is settled as not applied${blindReq.reason ? `: ${blindReq.reason.trim().replace(/\.$/, "")}` : ""}; send another value, or the same again, to retry.` : ""}`
                  : `${sentence(describeRequest(blindReq))} requested ${sgt(blindReq.since)} · ${requestText(blindReq.status)}${blindReq.reason ? ` (${blindReq.reason})` : ""}.${blindReq.followedAt ? ` A newer settings change followed ${sgt(blindReq.followedAt)}; the result below shows what the sensor did.` : ""}`}
              </p>
            )}
            {blindDone &&
              finite(blindDone.rb) !== null &&
              Math.abs(finite(blindDone.rb) - Number(blindDone.req)) > 1 && (
                <div className="field-alert" role="alert">
                  The sensor reads back {mm(blindDone.rb, 1)}, not{" "}
                  {mm(blindDone.req)}: the change did not take.
                </div>
              )}
            {!blindDone && blindResult && (
              <p className="field-caption">
                Last change {sgt(blindResult.t)}: {mm(blindResult.value.req)}{" "}
                requested, read back{" "}
                {finite(blindResult.value.rb) !== null
                  ? mm(blindResult.value.rb, 1)
                  : "nothing"}{" "}
                (the sensor answered {atText(blindResult.value.r)}).
              </p>
            )}
            <p className="field-caption">
              What the test tells you: with the blind zone at {mm(testValue)},
              a reading near{" "}
              {floorMm !== null ? mm(floorMm) : "the expected floor"} means the
              floor echo exists and the sensor was choosing a nearer one; a
              weak-echo result means the floor echo is blocked or too weak. The
              node changes it after the reading of its next wake, so the
              reading after that is the first one with the new value.
            </p>
          </div>
          <div className="field-radar-block">
            <h3 className="field-radar-heading">Echo profile capture</h3>
            <p className="field-caption" style={{ marginTop: 0 }}>
              About 4 kB, sent with an extra upload on the node's next wake.
              The sensor streams for up to 6 s; the node then switches the
              stream off and checks with a normal poll.
            </p>
            <div className="field-actions" style={{ marginTop: 0 }}>
              <button
                type="button"
                className="field-button primary"
                disabled={captureBusy || locked}
                onClick={capture}
              >
                {captureBusy ? "Sending…" : "Capture echo profile"}
              </button>
            </div>
            {captureError && (
              <div className="field-alert" role="alert">
                {captureError}
              </div>
            )}
            {captureReq && (
              <p className="field-radar-trail" role="status">
                {captured
                  ? `Captured ${sgt(capturedAt(captured))}; shown below.`
                  : captureRan
                    ? captureRan.value.st === "ok"
                      ? `The node captured a profile ${sgt(captureRan.t)}; waiting for it to arrive.`
                      : `The node tried ${sgt(captureRan.t)} but the sensor sent no complete profile (${captureRan.value.st || "none"}, ${Number(captureRan.value.b || 0).toLocaleString()} bytes in ${Number(captureRan.value.ms || 0).toLocaleString()} ms). Capture again to retry.`
                    : `${stillAsked(captureReq, settings) ? "Queued" : "Requested"} ${sgt(captureReq.since)} · ${requestText(captureReq.status)}${captureReq.reason ? ` (${captureReq.reason})` : ""}.${captureReq.followedAt ? ` A newer settings change followed ${sgt(captureReq.followedAt)}; if the node had not picked this one up, it was replaced.` : ""}`}
              </p>
            )}
            {fft && (fft.value.off === 0 || fft.value.off === false) && (
              <div className="field-alert" role="alert">
                After the capture {sgt(fft.t)} the node could not confirm that
                the sensor's raw output was switched off (poll afterwards:{" "}
                {statusText(fft.value.post)}). Check that the next readings
                arrive.
              </div>
            )}
          </div>
          <p className="field-caption" style={{ margin: 0 }}>
            {cellular
              ? "This node reports over cellular, so a command can take about an hour: the node picks it up at its next upload and carries it out on the following wake."
              : "The node picks a command up at its next upload and carries it out on the following wake; on cellular that can take about an hour."}
          </p>
        </div>
      </div>
      {streamed > 0 && (
        <p className="field-caption field-radar-streamed">
          {streamed === 1
            ? "1 of the newest captures was streamed in live mode"
            : `${streamed} of the newest captures were streamed in live mode`}
          ; they are in Live stream above, not in the picker below.
        </p>
      )}
      {bursted > 0 && (
        <p className="field-caption field-radar-streamed">
          {bursted === 1
            ? "1 of the newest captures came in a rich-wake burst"
            : `${bursted} of the newest captures came in rich-wake bursts`}
          ; they are in Rich readings and bursts above, not in the picker
          below.
        </p>
      )}
      <RadarProfile
        captures={captures}
        samples={samples}
        refresh={refresh}
        cfg={cfg}
        blindNow={current?.value ?? null}
        floorText={floorText}
        pipeText={pipeText}
        onFloorChange={changeFloor}
        onPipeChange={changePipe}
        focusId={captured?.id ?? null}
      />
    </section>
  );
}
